import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface StationPhotoLightboxProps {
  photos: Array<{ id: string; url: string; captured_at: string | null }>;
  initialIndex: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StationPhotoLightbox({
  photos,
  initialIndex,
  open,
  onOpenChange,
}: StationPhotoLightboxProps) {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex]);

  const next = useCallback(
    () => setIndex((i) => Math.min(i + 1, photos.length - 1)),
    [photos.length],
  );
  const prev = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev]);

  const current = photos[index];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl p-0 bg-black/95 border-0"
        showCloseButton={false}
      >
        <div className="relative h-[80vh] flex items-center justify-center">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 text-white hover:bg-white/10 z-10"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
          >
            <X className="size-5" />
          </Button>
          {index > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 z-10"
              onClick={prev}
              aria-label="Previous"
            >
              <ChevronLeft className="size-6" />
            </Button>
          )}
          {index < photos.length - 1 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 z-10"
              onClick={next}
              aria-label="Next"
            >
              <ChevronRight className="size-6" />
            </Button>
          )}
          {current && (
            <img
              src={current.url}
              alt=""
              className="max-h-full max-w-full object-contain"
            />
          )}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white text-xs bg-black/60 rounded-full px-3 py-1">
            {index + 1} / {photos.length}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
